import React, { useEffect, useRef, useState } from 'react';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { CreateAlertPopup } from 'components/Dashboard/Navigation/NotFeaturePopups';
import { useAuth0 } from '@auth0/auth0-react';
import { useCollapse } from 'react-collapsed';
import SelectedEvent from 'components/Dashboard/LiveFromParliament/SelectedEvent';
import time from 'utils/time';

const EventListItem = (props) => {
  const {
    item,
    selectedEvent,
    setSelectedEvent = () => {},
    isExpanded: isExpandedProp = false,
    setExpandedItem = () => {},
    emptyScreen,
  } = props;
  const {
    title,
    transcriberRunning,
    actualStartDateTime,
    actualEndDateTime,
    id,
    eventStartDate,
    dateTime,
    scheduledStartDateTime,
    displayStartDateTime,
  } = item ?? {};

  const [isExpanded, setOpen] = useState(isExpandedProp);
  const { getToggleProps, getCollapseProps } = useCollapse({
    isExpanded,
  });
  const { isAuthenticated } = useAuth0();
  const isMobile = useIsMobile();
  const isSmallMobile = useIsMobile(640);
  const isSelected = selectedEvent?.id === id;

  useEffect(() => {
    if (!isExpanded && isSelected) {
      setTimeout(() => {
        setOpen(id);
      }, 100);
    }
  }, [isSelected, isExpanded]);

  const listItemProps = isMobile
    ? getToggleProps({
        onClick: () => {
          setOpen(!isExpanded);
          setExpandedItem(isExpanded ? null : id);
          setSelectedEvent(isExpanded ? null : item);
        },
      })
    : {
        onClick: () => {
          setSelectedEvent(item);
        },
      };

  return (
    <>
      <li
        className={`my-2 cursor pointer p-0 ${isSelected ? 'bg-light-blue-important' : ''} event-list-item`}
        {...listItemProps}
      >
        <div className={`pr-3 pr-sm-4 d-flex ${emptyScreen ? '' : 'gap-15'}`}>
          <div
            className={`${emptyScreen ? 'event-empty-calendar-icon-container' : 'border-right'} d-flex align-items-center justify-content-center`}
            data-tooltip-id={'empty-calendar-list-tooltip'}
            data-tooltip-content={emptyScreen ? `${time(scheduledStartDateTime ?? eventStartDate).formatGeneralDate()}` : ''}
            style={
              emptyScreen
                ? null
                : {
                    width: isSmallMobile ? '90px' : '115px',
                    minWidth: isSmallMobile ? '90px' : '115px',
                  }
            }
          >
            {emptyScreen ? (
              <div className='event-calendar-icon-container'>
                <span className='icon-calendar' />
                <span className='event-empty-calendar-date'>
                  {time(scheduledStartDateTime ?? eventStartDate).format('D')}
                </span>
              </div>
            ) : (
              <>
                {transcriberRunning ? (
                  <span className='py-1 px-3 background-medium-sea-green text-white rounded event-item-live-badge'>
                    <span
                      className='mr-1 paragraph-p1 title-h3 d-inline-block'
                      style={{ transform: 'translateX(-4px)' }}
                    >
                      •
                    </span>
                    Live
                  </span>
                ) : actualStartDateTime ? (
                  <>
                    {actualEndDateTime ? (
                      <span className='py-1 px-3 danger-background text-white rounded event-item-live-badge'>
                        Ended
                      </span>
                    ) : (
                      <span className={`paragraph-p1 title-h3 ${isSelected ? 'text-white' : ''}`}>
                        {time(actualStartDateTime).format('H:mm a')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className={`paragraph-p1 title-h3 ${isSelected ? 'text-white' : ''}`}>
                    {time(dateTime ?? eventStartDate ?? displayStartDateTime).format('H:mm a')}
                  </span>
                )}
              </>
            )}
          </div>

          <div className='d-flex align-items-center justify-content-between w-100'>
            <h4
              className={`mb-0 flex-grow-1 content-item-title font-weight-normal title-h5 specific-top-padding-23 specific-bottom-padding-22 ${isSelected ? 'text-white' : ''}`}
            >
              {title}
            </h4>
            {!isAuthenticated && !actualEndDateTime && <AlertActionBox isSelected={isSelected} />}
          </div>
        </div>
      </li>
      {isMobile && isSelected && (
        <div {...getCollapseProps()}>
          <div className='flex-grow-1 overscroll-behavior-contain'>
            <SelectedEvent selectedEvent={selectedEvent} />
          </div>
        </div>
      )}
    </>
  );
};

const AlertActionBox = ({ isSelected }) => {
  const [showActions, setShowActions] = useState(false);
  const [isOpenCreateAlertPopup, setIsOpenCreateAlertPopup] = useState(false);

  const actionsElement = useRef(null);
  const hideActions = () => {
    setShowActions(false);
  };
  useOutsideElement(actionsElement, hideActions);

  return (
    <>
      <div className='position-relative pl-3' ref={actionsElement}>
        <button
          className={`general-button circle-button hover-background-gray-05 p-1`}
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(!showActions);
          }}
        >
          <span className={`icon-three-dots ${isSelected ? 'text-white' : 'light-40-text'}`} />
        </button>
        {showActions && (
          <div
            className='box box-shadow py-1 w-auto position-absolute right-0 position-relative'
            style={{ left: '-113px' }}
          >
            <div className='share-container d-block position-relative'>
              <div className='share-container-items flex-column'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActions(false);
                    setIsOpenCreateAlertPopup(true);
                  }}
                  className='px-3 py-1 bg-main-white general-button hovered-button link-hover reset-link nowrap-item'
                >
                  <span className='d-inline-block icon-alert-medium paragraph-p2 d-inline-block-centered centered-2 icon-alert-medium paragraph-p2 specific-right-margin-8' />
                  Create an alert
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <CreateAlertPopup isOpen={isOpenCreateAlertPopup} setIsOpen={setIsOpenCreateAlertPopup} />
    </>
  );
};

export default EventListItem;
